/**
 * The phone Knowledge Archive's list view — ROUTE_INVENTORY §2.10 #49.
 *
 * **Sorted by importance descending**, which is what lets it stand in for the
 * map: the graph conveys prominence spatially (a hub sits in the
 * middle of its cluster), and a list in API order would throw that away and
 * leave the user scrolling an arbitrary sequence. The colour dot carries the
 * domain, the one other thing the graph encodes visually.
 *
 * One of two views on a phone, not a substitute any more: the graph is the
 * default and this is the other segment of the header's Graph | List toggle.
 * It stays because it does what the canvas cannot - a screen reader reads it,
 * and a thumb scans it faster than it pans a map. So the "graph view is best
 * on a larger screen" line that used to head it is gone: there is a graph.
 *
 * The list owns its scroll. The page around it is a fixed-height column that
 * clips, so without `overflow-y-auto` every concept below the first screen
 * was unreachable on a phone.
 */

import { useMemo } from 'react';
import { InlineMarkdown } from '@/components/core/markdown/markdown';
import { DOMAIN_HEX, colorSlotForDomain } from '@/utils/mindspaceDisplay';
import type { KnowledgeGraphNode } from '@/lib/api-client';

interface ConceptMobileListProps {
  nodes: KnowledgeGraphNode[];
  onNodeClick: (id: string) => void;
}

export function ConceptMobileList({
  nodes,
  onNodeClick,
}: ConceptMobileListProps) {
  const sorted = useMemo(
    () => [...nodes].sort((a, b) => (b.importance ?? 0) - (a.importance ?? 0)),
    [nodes]
  );
  return (
    <div className='h-full overflow-y-auto'>
      <ul className='divide-y divide-border'>
        {sorted.map(node => {
          const color = DOMAIN_HEX[colorSlotForDomain(node.domain_tags?.[0])];
          return (
            <li key={node.id}>
              <button
                onClick={() => onNodeClick(node.id)}
                className='flex min-h-14 w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted'
              >
                <span
                  className='h-3 w-3 flex-shrink-0 rounded-full'
                  style={{ backgroundColor: color }}
                />
                <div className='min-w-0 flex-1'>
                  <p className='truncate text-sm font-medium text-foreground'>
                    <InlineMarkdown>{node.name}</InlineMarkdown>
                  </p>
                  {node.domain_tags && node.domain_tags.length > 0 && (
                    <p className='truncate text-xs text-muted-foreground'>
                      {node.domain_tags.join(' · ')}
                    </p>
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
